import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Amplify } from "aws-amplify";
import {
  fetchAuthSession,
  getCurrentUser,
  signInWithRedirect,
  signOut,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";

import {
  amplifyAuthConfig,
  authDisabled,
  cognitoConfigured,
  cognitoHostedLoginUrl,
  cognitoHostedLogoutUrl,
} from "@/lib/authConfig";
import { setAuthHeaderProviders } from "@/lib/authHeaders";
import { clearAziendaSilently } from "@/lib/azienda";

const IMPERSONATE_KEY = "rentri.impersonateEmail";
const DEV_USER_KEY = "rentri.devUser";

export type AuthState = {
  ready: boolean;
  authenticated: boolean;
  email: string;
  realEmail: string;
  isAdmin: boolean;
  impersonating: boolean;
  impersonateEmail: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  startImpersonate: (targetEmail: string) => Promise<void>;
  stopImpersonate: () => Promise<void>;
  setDevUser: (email: string) => void;
};

const AuthContext = createContext<AuthState | null>(null);

if (cognitoConfigured() && !authDisabled) {
  Amplify.configure(amplifyAuthConfig());
}

async function loadCognitoProfile(): Promise<{
  email: string;
  groups: string[];
} | null> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken;
    if (!token) return null;
    const payload = token.payload as Record<string, unknown>;
    const email = String(
      payload.email ?? payload["cognito:username"] ?? payload.sub ?? "",
    )
      .trim()
      .toLowerCase();
    const groups = payload["cognito:groups"];
    const groupList = Array.isArray(groups)
      ? groups.map(String)
      : typeof groups === "string"
        ? [groups]
        : [];
    return { email, groups: groupList };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(authDisabled);
  const [authenticated, setAuthenticated] = useState(authDisabled);
  const [email, setEmail] = useState("");
  const [realEmail, setRealEmail] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [impersonateEmail, setImpersonateEmail] = useState<string | null>(() =>
    localStorage.getItem(IMPERSONATE_KEY),
  );

  const impersonating = Boolean(impersonateEmail);

  const refresh = useCallback(async () => {
    if (authDisabled) {
      const dev = (localStorage.getItem(DEV_USER_KEY) || "admin@quality-safety.it").toLowerCase();
      const imp = localStorage.getItem(IMPERSONATE_KEY);
      setRealEmail(dev);
      setEmail(imp || dev);
      setIsAdmin(
        dev === "admin@quality-safety.it" || dev === "paolo.comitini@glisco.it",
      );
      setAuthenticated(true);
      setReady(true);
      return;
    }
    if (!cognitoConfigured()) {
      setReady(true);
      setAuthenticated(false);
      return;
    }
    try {
      await getCurrentUser();
      const profile = await loadCognitoProfile();
      if (!profile?.email) {
        setAuthenticated(false);
        setReady(true);
        return;
      }
      const admin = profile.groups.includes("rentri-admins");
      setRealEmail(profile.email);
      setIsAdmin(admin);
      const imp = localStorage.getItem(IMPERSONATE_KEY);
      setImpersonateEmail(imp);
      setEmail(imp && admin ? imp : profile.email);
      setAuthenticated(true);
    } catch {
      setAuthenticated(false);
      setEmail("");
      setRealEmail("");
      setIsAdmin(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const unsub = Hub.listen("auth", () => {
      void refresh();
    });
    return () => unsub();
  }, [refresh]);

  useEffect(() => {
    setAuthHeaderProviders({
      getAccessToken: async () => {
        if (authDisabled || !cognitoConfigured()) return null;
        const session = await fetchAuthSession();
        // Id token include email e aud richiesti dal backend JWT.
        return (
          session.tokens?.idToken?.toString() ??
          session.tokens?.accessToken?.toString() ??
          null
        );
      },
      getDevUser: () =>
        (localStorage.getItem(DEV_USER_KEY) || "admin@quality-safety.it").toLowerCase(),
      getImpersonateEmail: () => localStorage.getItem(IMPERSONATE_KEY),
    });
  }, []);

  const login = useCallback(async () => {
    if (authDisabled) {
      const v = prompt("Email dev (X-Dev-User)", realEmail || "admin@quality-safety.it");
      if (v) {
        localStorage.setItem(DEV_USER_KEY, v);
        await refresh();
      }
      return;
    }
    await signInWithRedirect({ options: { lang: "it" } });
  }, [realEmail, refresh]);

  const logout = useCallback(async () => {
    localStorage.removeItem(IMPERSONATE_KEY);
    clearAziendaSilently();
    setImpersonateEmail(null);
    if (authDisabled) {
      setAuthenticated(false);
      setEmail("");
      setRealEmail("");
      setIsAdmin(false);
      setReady(true);
      const login = cognitoHostedLoginUrl();
      window.location.assign(login ?? "/login");
      return;
    }
    try {
      await signOut({ global: true });
    } catch {
      /* token locali già assenti */
    }
    setAuthenticated(false);
    setEmail("");
    setRealEmail("");
    setIsAdmin(false);
    const hostedLogout = cognitoHostedLogoutUrl();
    if (hostedLogout) {
      window.location.assign(hostedLogout);
      return;
    }
    window.location.assign("/auth/exit");
  }, []);

  const startImpersonate = useCallback(
    async (targetEmail: string) => {
      const target = targetEmail.trim().toLowerCase();
      localStorage.setItem(IMPERSONATE_KEY, target);
      setImpersonateEmail(target);
      setEmail(target);
      try {
        await import("@/api/client").then(({ api }) =>
          api.post("/users/admin/impersonate", {
            target_email: target,
            action: "start",
          }),
        );
      } catch {
        /* audit opzionale */
      }
    },
    [],
  );

  const stopImpersonate = useCallback(async () => {
    localStorage.removeItem(IMPERSONATE_KEY);
    // L'azienda gestita appartiene al gestore impersonato: non ha senso per l'admin.
    clearAziendaSilently();
    setImpersonateEmail(null);
    setEmail(realEmail);
    try {
      await import("@/api/client").then(({ api }) =>
        api.post("/users/admin/impersonate", {
          target_email: realEmail,
          action: "stop",
        }),
      );
    } catch {
      /* */
    }
  }, [realEmail]);

  const setDevUser = useCallback(
    (v: string) => {
      localStorage.setItem(DEV_USER_KEY, v);
      void refresh();
    },
    [refresh],
  );

  const value = useMemo<AuthState>(
    () => ({
      ready,
      authenticated,
      email,
      realEmail,
      isAdmin,
      impersonating,
      impersonateEmail,
      login,
      logout,
      startImpersonate,
      stopImpersonate,
      setDevUser,
    }),
    [
      ready,
      authenticated,
      email,
      realEmail,
      isAdmin,
      impersonating,
      impersonateEmail,
      login,
      logout,
      startImpersonate,
      stopImpersonate,
      setDevUser,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth richiede AuthProvider");
  return ctx;
}
